import { makeAutoObservable } from 'mobx';
import ConfirmManager, { ConfirmActionsTypes } from 'src/libs/ui-kit/confirm-dialog/ConfirmManager';
import { VisibilityStore } from 'src/modules/users/stores/VisibilityStore';
import ToastifyService from 'src/services/ToastifyService';
import { UserDeleteRestoreAddressModal } from '../components/modals/UserDeleteRestoreAddressModal';
import { IUserAddressesServiceProvider } from '../service-provider';
import { IUserAddressEditForm, IUserAddressesListItem, IUserAddressForm, IUserViewAddress } from '../service-provider';

export class UserAddressesWidgetStore {
  hasError = false;

  addressesAreLoading = false;
  addresses: IUserAddressesListItem[] = [];

  viewAddressModal = new VisibilityStore();
  chosenViewAddress: IUserViewAddress | undefined;

  isModalLoading = false;

  editAddressModal = new VisibilityStore();
  createAddressModal = new VisibilityStore();
  chosenAddress: IUserAddressEditForm | undefined;

  awaitingChangingAddresses = new Set();

  constructor(readonly service: IUserAddressesServiceProvider) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  async loadAddresses(): Promise<void> {
    this.addressesAreLoading = true;
    this.hasError = false;

    try {
      const { data } = await this.service.searchAddresses();

      this.addresses = data ?? [];
    } catch {
      this.addresses = [];
      this.hasError = true;
    } finally {
      this.addressesAreLoading = false;
    }
  }

  async toggleEditAddressModal(addressId: string | null | undefined): Promise<void> {
    this.editAddressModal.show();

    await this.loadEditAddress(addressId);
  }

  async createAddress(newAddress: IUserAddressForm): Promise<void> {
    this.isModalLoading = true;
    this.hasError = false;

    try {
      const { data } = await this.service.createAddress({
        newAddress,
      });

      this.addresses = data ?? [];

      ToastifyService.toast(`Успешно создан адрес: ${newAddress.value?.value}`, {
        type: 'success',
      });
    } catch {
      ToastifyService.toast('Не удалось создать адрес', {
        type: 'error',
      });
    } finally {
      this.isModalLoading = false;
      this.createAddressModal.hide();
    }
  }

  async updateAddress(address: IUserAddressForm): Promise<void> {
    this.isModalLoading = true;
    this.hasError = false;

    try {
      const { data } = await this.service.updateAddress({
        address,
      });

      this.addresses = data ?? [];

      ToastifyService.toast(`Успешно обновлен адрес: ${address.value?.value}`, {
        type: 'success',
      });
    } catch {
      ToastifyService.toast('Не удалось обновить адрес', {
        type: 'error',
      });
    } finally {
      this.isModalLoading = false;
      this.editAddressModal.hide();
    }
  }

  async selectAddressForViewing(addressId: string | undefined | null) {
    this.viewAddressModal.show();

    await this.retrieveViewAddress(addressId);
  }

  async deleteAddress(address: IUserAddressesListItem): Promise<void> {
    return ConfirmManager.show<IUserAddressesListItem>({
      title: 'Удалить адрес',
      message: UserDeleteRestoreAddressModal,
      confirmButtonLabel: 'Удалить',
      onConfirm: async () => {
        await this.handleDeleteAddress(address);
      },
      variant: ConfirmActionsTypes.DANGER,
      data: address,
    }).then(() => {
      ConfirmManager.hide();
    });
  }

  public async restoreAddress(address: IUserAddressesListItem): Promise<void> {
    return ConfirmManager.show<IUserAddressesListItem>({
      title: 'Восстановить адрес',
      message: UserDeleteRestoreAddressModal,
      confirmButtonLabel: 'Восстановить',
      onConfirm: async () => {
        await this.handleRestore(address);
      },
      variant: ConfirmActionsTypes.SUCCESS,
      data: address,
    }).then(() => {
      ConfirmManager.hide();
    });
  }

  private async loadEditAddress(addressId: string | null | undefined): Promise<void> {
    this.isModalLoading = true;
    this.hasError = false;

    try {
      const data = await this.service.retrieveEditAddress({
        addressId,
      });

      this.chosenAddress = data;
    } catch {
      this.chosenAddress = undefined;
      this.hasError = true;
    } finally {
      this.isModalLoading = false;
    }
  }

  private async retrieveViewAddress(addressId: string | undefined | null): Promise<void> {
    this.isModalLoading = true;
    this.hasError = false;

    try {
      const data = await this.service.retrieveViewAddress({
        addressId,
      });

      this.chosenViewAddress = { ...data, id: addressId };
    } catch {
      this.chosenViewAddress = undefined;
      this.hasError = true;
    } finally {
      this.isModalLoading = false;
    }
  }

  private async handleRestore(address: IUserAddressesListItem) {
    this.awaitingChangingAddresses.add(address.id!);
    this.hasError = false;

    try {
      const { data } = await this.service.restoreAddress({
        addressId: address.id,
      });

      this.addresses = data ?? [];

      ToastifyService.toast(`Успешно восстановлен адрес: ${address.value}, тип: ${address.addressTypeLabel}`, {
        type: 'success',
      });
    } catch (error) {
      this.hasError = true;
    } finally {
      this.awaitingChangingAddresses.delete(address.id!);
    }

    return address;
  }

  private async handleDeleteAddress(address: IUserAddressesListItem) {
    this.awaitingChangingAddresses.add(address.id!);
    this.hasError = false;

    try {
      const { data } = await this.service.deleteAddress({
        addressId: address.id,
      });

      this.addresses = data ?? [];

      ToastifyService.toast(`Успешно удален адрес: ${address.value}, тип: ${address.addressTypeLabel}`, {
        type: 'success',
      });
    } catch (error) {
      ToastifyService.toast('Не удалось удалить адрес', {
        type: 'error',
      });
    } finally {
      this.awaitingChangingAddresses.delete(address.id!);
    }

    return address;
  }
}
